import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import type { StrictPopupProps } from 'semantic-ui-react';
import { Label, Popup } from 'semantic-ui-react';

import { remToPx } from '@utils/dom-utils';

import s from './truncate-list.scss';

type BaseProps<T> = {
  list: Array<T>;
  itemPaddingRem?: number;
  itemGapRem?: number;
  containerPaddingRem?: number;
  className?: string;
  /**
   * Class applied to the inner list container — overrides the default
   * flex layout (e.g. to right-align the items).
   */
  containerClassName?: string;
  popupProps?: StrictPopupProps;
};

/**
 * When T is a string everything is optional
 */
type StringProps<T extends string> = {
  renderItem?: (item: T) => ReactNode;
  measureItem?: (item: T) => number;
  renderHiddenItem?: (item: T) => ReactNode;
  renderPopup?: (trigger: ReactNode, remainingItems: T[]) => ReactNode;
};

/**
 * When T is an object, we need to ensure that renderItem, measureItem and either renderHiddenItem or renderPopup is provided
 * measureItem is used to calculate the width of the item as we can't measure the width of React components directly
 */
type ObjectProps<T> = {
  renderItem: (item: T) => ReactNode;
  measureItem: (item: T) => number;
  renderPopup?: (trigger: ReactNode, remainingItems: T[]) => ReactNode;
  renderHiddenItem?: (item: T) => ReactNode;
} & (
  | { renderHiddenItem: (item: T) => ReactNode }
  | { renderPopup: (trigger: ReactNode, remainingItems: T[]) => ReactNode }
);

export type TruncateListProps<T> = (T extends string
  ? StringProps<T>
  : ObjectProps<T>) &
  BaseProps<T>;

export const getTextWidth = (text: string) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  return context?.measureText(text).width ?? 0;
};

const isString = (item: unknown): item is string => typeof item === 'string';

function TruncateList<T>({
  list,
  className,
  containerClassName,
  popupProps = {},
  itemPaddingRem = 1,
  itemGapRem = 0.25,
  containerPaddingRem = 0.25,
  renderItem: propsRenderItem,
  renderHiddenItem: propsRenderHiddenItem,
  renderPopup: propsRenderPopup,
  measureItem,
}: TruncateListProps<T>): JSX.Element | null {
  useStyles(s);

  const renderItem = useCallback(
    (item: T) => {
      if (propsRenderItem) {
        return propsRenderItem(item);
      }
      // This default case only applies when T is string
      // TypeScript can't infer this, but at runtime this is safe
      return (
        <Label key={String(item)} size="small" className={s.listItemLabel}>
          {String(item)}
        </Label>
      );
    },
    [propsRenderItem],
  );

  const renderHiddenItem = useCallback(
    (item: T) => {
      if (propsRenderHiddenItem) {
        return propsRenderHiddenItem(item);
      }
      // Default for strings only - at runtime we know this is safe
      return <div key={String(item)}>{String(item)}</div>;
    },
    [propsRenderHiddenItem],
  );

  const renderPopup = useCallback(
    (trigger: ReactNode, remainingItems: T[]) =>
      propsRenderPopup ? (
        propsRenderPopup(trigger, remainingItems)
      ) : (
        <Popup
          role="dialog"
          key="list-with-remainder-popup"
          className={classNames(s.root, className)}
          trigger={trigger}
          content={remainingItems.map(item => renderHiddenItem(item))}
          position="bottom left"
          basic
          on="hover"
          hoverable
          // Provide a fallback placement if popup gets clipped on the right
          popperModifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-end'],
              },
            },
          ]}
          {...popupProps}
        />
      ),
    [propsRenderPopup, popupProps, renderHiddenItem, className],
  );

  const containerRef = useRef<HTMLDivElement | null>(null);

  const result = useMemo(() => {
    if (list.length) {
      const containerWidth = containerRef?.current?.offsetWidth || 0;
      return list.reduce(
        (acc, curr) => {
          if (acc.toHide.length) {
            return {
              ...acc,
              toHide: [...acc.toHide, curr],
            };
          }
          const remainingCount = list.length - acc.toShow.length;
          const renderedLabel = renderItem(curr);
          // Use measureItem if provided, otherwise getTextWidth for strings, default for objects
          const textWidth = measureItem
            ? measureItem(curr)
            : isString(curr)
              ? getTextWidth(curr)
              : 100;
          const itemWidth =
            remToPx(itemPaddingRem) +
            textWidth +
            (acc.toShow.length ? remToPx(itemGapRem) : 0);
          const andXMoreWidth = remainingCount
            ? getTextWidth(`and ${remainingCount} more`) + remToPx(itemGapRem)
            : 0;
          if (
            acc.width + itemWidth + andXMoreWidth > containerWidth &&
            acc.toShow?.length
          ) {
            return {
              ...acc,
              toHide: [...acc.toHide, curr],
            };
          }
          return {
            ...acc,
            width: acc.width + itemWidth,
            toShow: [...acc.toShow, renderedLabel],
          };
        },
        {
          width: remToPx(containerPaddingRem),
          toShow: [] as ReactNode[],
          toHide: [] as T[],
        },
      );
    }
    return { toShow: [] as ReactNode[], toHide: [] as T[] };
  }, [
    list,
    containerPaddingRem,
    renderItem,
    measureItem,
    itemPaddingRem,
    itemGapRem,
  ]);

  const { toShow, toHide } = result;

  if (list?.length) {
    return (
      <div
        className={classNames(s.list, containerClassName)}
        ref={containerRef}
      >
        {toShow}
        {toHide?.length
          ? renderPopup(
              <span className={s.andXMore}>and {toHide.length} more</span>,
              toHide,
            )
          : null}
      </div>
    );
  }
  return null;
}

export default TruncateList;
